import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import EventsSlider from "../HomeComponents/EventsSlider";
import { getNews } from "../../services/api";
import { useLanguage } from "../../contexts/LanguageContext";
import { translateNewsArray } from "../../utils/translationHelper";
import DOMPurify from 'dompurify'

const ITEMS_PER_PAGE = 6;

function AllNews() {
  const [currentPage, setCurrentPage] = useState(1);
  const navigate = useNavigate();
  const [allNews, setAllNews] = useState([]);
  const [showLanguageMenu, setShowLanguageMenu] = useState(false);
  const totalPages = Math.ceil(allNews.length / ITEMS_PER_PAGE);

  // Получаем языковой контекст
  const { currentLanguage, languages, changeLanguage } = useLanguage();

  // Месяцы на разных языках
  const months = {
    kz: [
      "ҚАҢТАР",
      "Ақпан",
      "Наурыз",
      "Сәуір",
      "Мамыр",
      "Маусым",
      "Шілде",
      "Тамыз",
      "Қыркүйек",
      "Қазан",
      "Қараша",
      "Желтоқсан",
    ],
    ru: [
      "ЯНВАРЬ",
      "Февраль",
      "Март",
      "Апрель",
      "Май",
      "Июнь",
      "Июль",
      "Август",
      "Сентябрь",
      "Октябрь",
      "Ноябрь",
      "Декабрь",
    ],
    en: [
      "JANUARY",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ],
  };

  // Переводы текстов
  const translations = {
    kz: {
      newsTitle: "Кітапхана жаңалықтары",
      noTitle: "Атауы жоқ",
      noDescription: "Сипаттама жоқ",
      prev: "Артқа",
      next: "Алға",
    },
    ru: {
      newsTitle: "Новости библиотеки",
      noTitle: "Без названия",
      noDescription: "Описание отсутствует",
      prev: "Назад",
      next: "Вперед",
    },
    en: {
      newsTitle: "Library News",
      noTitle: "No Title",
      noDescription: "No Description",
      prev: "Previous",
      next: "Next",
    },
  };

  // Получаем текущие переводы
  const t = translations[currentLanguage] || translations.kz;
  const currentMonths = months[currentLanguage] || months.kz;

  // Функция форматирования даты
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const day = date.getDate();
    const month = currentMonths[date.getMonth()];
    const year = date.getFullYear();
    return `${day} ${month}, ${year}`;
  };

  const handleLanguageChange = (languageCode) => {
    changeLanguage(languageCode);
    setShowLanguageMenu(false);
  };

  useEffect(() => {
    const loadAllNews = async () => {
      // Получаем новости со всеми переводами
      const res = await getNews();
      if (res.news) {
        // Применяем переводы для текущего языка
        const translatedNews = translateNewsArray(res.news, currentLanguage);
        // Сортируем новости по дате в убывающем порядке (новые сверху)
        const sortedNews = translatedNews.sort((a, b) => new Date(b.date) - new Date(a.date));
        setAllNews(sortedNews);
      }
    };
    loadAllNews();
  }, [currentLanguage]); // Перезагружаем новости при смене языка

  const currentItems = allNews.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const handlePageClick = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCardClick = (id) => {
    navigate(`/news/${id}`);
  };

  // Функция для генерации массива видимых страниц
  const getPageNumbers = () => {
    const delta = 2; // Сколько страниц показывать слева и справа от текущей
    const range = [];
    const rangeWithDots = [];

    // Если страниц мало, показываем все
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    // Добавляем страницы вокруг текущей
    for (
      let i = Math.max(2, currentPage - delta);
      i <= Math.min(totalPages - 1, currentPage + delta);
      i++
    ) {
      range.push(i);
    }

    // Всегда показываем первую страницу
    if (currentPage - delta > 2) {
      rangeWithDots.push(1, '...');
    } else {
      rangeWithDots.push(1);
    }

    rangeWithDots.push(...range);

    // Всегда показываем последнюю страницу
    if (currentPage + delta < totalPages - 1) {
      rangeWithDots.push('...', totalPages);
    } else if (totalPages > 1) {
      rangeWithDots.push(totalPages);
    }

    return rangeWithDots;
  };

  return (
    <div className="news-section">
      <div className="news-title-line">
        <span className="line-news"></span>
        <h2 className="newsMain">{t.newsTitle}</h2>
        <span className="line-news"></span>
      </div>

      <div className="news-grid">
        {currentItems.map((item) => (
          <div
            className="news-card"
            key={item.id}
            onClick={() => handleCardClick(item.id)}
            style={{ cursor: "pointer" }}
          >
            <div className="news-image">
              <img
                src={`${process.env.REACT_APP_API_URL}${item.img_url}`}
                alt={item.title || t.noTitle}
              />
            </div>
            <div className="news-content">
              <p className="news-date">{formatDate(item.date)}</p>
              <h3 className="news-title">{item.title || t.noTitle}</h3>
              <div className="news-footer">
                <span className="news-category">
                  <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(item.description, { ALLOWED_TAGS: ['p'], ALLOWED_ATTR: [] }) || t.noDescription }} />
                </span>
                {item.instagram &&
                  <a href={item.instagram} target="_blank" rel="noopener noreferrer" className="instagram-link">
                    <i className="fab fa-instagram"></i>
                  </a>
                }
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Улучшенная Pagination */}
      {totalPages > 1 && (
        <div className="pagination">
          {/* Кнопка "Назад" */}
          <button
            className="pagination-btn pagination-arrow"
            onClick={() => handlePageClick(currentPage - 1)}
            disabled={currentPage === 1}
          >
            <span className="arrow-icon">←</span>
            <span className="arrow-text">{t.prev}</span>
          </button>

          {/* Номера страниц */}
          <div className="pagination-numbers">
            {getPageNumbers().map((page, index) => (
              page === '...' ? (
                <span key={`dots-${index}`} className="pagination-dots">
                  ...
                </span>
              ) : (
                <button
                  key={page}
                  className={`pagination-btn page-number ${currentPage === page ? "active" : ""}`}
                  onClick={() => handlePageClick(page)}
                >
                  {page}
                </button>
              )
            ))}
          </div>

          {/* Кнопка "Вперед" */}
          <button
            className="pagination-btn pagination-arrow"
            onClick={() => handlePageClick(currentPage + 1)}
            disabled={currentPage === totalPages}
          >
            <span className="arrow-text">{t.next}</span>
            <span className="arrow-icon">→</span>
          </button>
        </div>
      )}

      <EventsSlider />
    </div>
  );
}

export default AllNews;